const CONTACT_EMAIL = "jobs@theotautomations.com";

const LegalLayout = ({ eyebrow, title, updated, testId, children }) => (
  <main className="min-h-screen bg-[#05050A]" data-testid={testId}>
    <section className="relative pt-28 pb-10 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="tech-hero-grid" />
      <div className="relative max-w-3xl mx-auto">
        <span className="tag px-3 py-1 bg-[#D9F839]/10 text-[#D9F839] rounded-full mb-5 inline-block">{eyebrow}</span>
        <h1 className="text-4xl sm:text-5xl text-white mb-4" style={{ fontFamily: "Unbounded, sans-serif" }} data-testid={`${testId}-title`}>
          {title}
        </h1>
        <p className="text-sm text-[#7A7A85]">Last updated: {updated}</p>
      </div>
    </section>
    <section className="pb-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto panel-card p-6 md:p-10 space-y-8" data-testid={`${testId}-body`}>
        {children}
      </div>
    </section>
  </main>
);

const Clause = ({ heading, children }) => (
  <div>
    <h2 className="text-base md:text-lg text-white mb-3" style={{ fontFamily: "Unbounded, sans-serif" }}>{heading}</h2>
    <div className="space-y-3 text-sm text-[#B8B9C3] leading-relaxed">{children}</div>
  </div>
);

export function PrivacyPolicyPage() {
  return (
    <LegalLayout eyebrow="Legal" title="Privacy Policy" updated="June 2026" testId="privacy-page">
      <Clause heading="Who we are">
        <p>
          theotautomations.com ("we", "us") operates an AI training job board. This policy explains what personal data we
          collect, why we collect it, and what you can ask us to do with it.
        </p>
      </Clause>
      <Clause heading="Data we collect">
        <p>We keep the collection deliberately small:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong className="text-white">Newsletter email address</strong> — only if you subscribe, together with the page you subscribed from and the date.</li>
          <li><strong className="text-white">Contact form details</strong> — your name, email, subject and message, so we can reply.</li>
          <li><strong className="text-white">Job posting details</strong> — the listing content and billing email when a company buys a featured listing.</li>
          <li><strong className="text-white">Basic usage data</strong> — anonymous, aggregated page analytics. We do not build advertising profiles.</li>
        </ul>
      </Clause>
      <Clause heading="How we use it">
        <p>
          To send the newsletter you asked for, to answer your messages, to publish and manage job listings, and to
          detect abuse. We do not sell your personal data, and we do not share your email address with the platforms
          listed on the board.
        </p>
      </Clause>
      <Clause heading="Processors we rely on">
        <ul className="list-disc pl-5 space-y-2">
          <li><strong className="text-white">Resend</strong> — delivers our newsletter and transactional email.</li>
          <li><strong className="text-white">Stripe</strong> — processes payments for featured listings. Card details go directly to Stripe and never reach our servers.</li>
          <li><strong className="text-white">Cloud hosting and database providers</strong> — store the site and its data.</li>
        </ul>
      </Clause>
      <Clause heading="Cookies">
        <p>
          We use only what the site needs to work — a session token when an administrator signs in, and anonymous
          analytics. No third-party advertising cookies are set by us.
        </p>
      </Clause>
      <Clause heading="Retention">
        <p>
          Newsletter subscriptions are kept until you unsubscribe (every email contains a one-click unsubscribe link).
          Contact messages are kept for up to 24 months. Payment records are kept as long as tax law requires.
        </p>
      </Clause>
      <Clause heading="Your rights">
        <p>
          You can ask us to access, correct, export or delete your personal data, and you can object to processing.
          Email <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#D9F839]">{CONTACT_EMAIL}</a> and we will respond
          within 30 days.
        </p>
      </Clause>
      <Clause heading="External links">
        <p>
          The board links to third-party platforms, some through partner links. Once you leave this site, the privacy
          policy of that platform applies. We are not responsible for how those platforms handle your data.
        </p>
      </Clause>
      <Clause heading="Changes">
        <p>
          If this policy changes materially we will update the date above and, where appropriate, tell newsletter
          subscribers.
        </p>
      </Clause>
    </LegalLayout>
  );
}

export function TermsPage() {
  return (
    <LegalLayout eyebrow="Legal" title="Terms and Conditions" updated="June 2026" testId="terms-page">
      <Clause heading="1. Agreement">
        <p>
          By using theotautomations.com you agree to these terms. If you do not agree, please do not use the site. These
          terms apply to job seekers browsing the board and to companies posting listings.
        </p>
      </Clause>
      <Clause heading="2. What this site is">
        <p>
          We are an independent directory and job board. We are not an employer, an agency, or a party to any agreement
          between you and a platform listed here. We do not guarantee that you will be accepted, hired, or paid by any
          platform.
        </p>
      </Clause>
      <Clause heading="3. Accuracy of listings">
        <p>
          Listings and platform descriptions are compiled from public sources and from the companies themselves. Pay
          rates, availability and requirements change frequently. Always verify the details on the platform's own site
          before applying or accepting work. Tell us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#D9F839]">{CONTACT_EMAIL}</a> if
          something is out of date and we will correct or remove it.
        </p>
      </Clause>
      <Clause heading="4. No employment or financial advice">
        <p>
          Nothing on this site is a job offer, a promise of income, or financial, legal or tax advice. Earnings examples
          are illustrations, not projections. You are responsible for your own taxes and for checking that the work is
          permitted where you live.
        </p>
      </Clause>
      <Clause heading="5. Paid featured listings">
        <p>
          Companies may purchase featured placement for a listing. Payment is processed by Stripe and grants placement
          only — it does not buy editorial endorsement. Featured listings must be genuine paid opportunities. We may
          reject or remove any listing that is misleading, discriminatory, charges applicants a fee, or breaks the law,
          and we may refuse a refund where a listing is removed for breaching these terms. Unused listing credits expire
          twelve months after purchase.
        </p>
      </Clause>
      <Clause heading="6. Acceptable use">
        <p>You agree not to scrape the site at scale, resell our listings database, attempt to breach the admin area, or upload malicious content through any form on the site.</p>
      </Clause>
      <Clause heading="7. Partner links">
        <p>
          Some outbound links are partner or referral links, meaning we may earn a commission or bonus if you sign up.
          This never affects whether a platform is listed or how we describe it.
        </p>
      </Clause>
      <Clause heading="8. Intellectual property">
        <p>
          The site design, written guides and curated directories are ours. Company names and logos belong to their
          respective owners and are shown for identification only. You may link to our pages; do not republish our
          content wholesale without permission.
        </p>
      </Clause>
      <Clause heading="9. Liability">
        <p>
          The site is provided "as is". To the fullest extent permitted by law we are not liable for lost earnings,
          unpaid work, or any indirect loss arising from your use of the site or from your dealings with a third-party
          platform.
        </p>
      </Clause>
      <Clause heading="10. Changes and contact">
        <p>
          We may update these terms; continued use after an update means you accept the revised version. Questions go
          to <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#D9F839]">{CONTACT_EMAIL}</a>.
        </p>
      </Clause>
    </LegalLayout>
  );
}
